import { useEffect, useRef, useState, useCallback, type JSX } from "react";
import { getSongUrl, getStats } from "../lib/shoutcast";
import { PauseIcon, PlayIcon } from "lucide-react";
import { decodeHtmlEntities } from "../lib/utils";

const MAX_RETRIES = 10;
const RETRY_BASE_DELAY = 1000;
const RETRY_MULTIPLIER = 1.5;
const MAX_RETRY_DELAY = 10000;

interface StatsData {
    songtitle?: string;
    nexttitle?: string;
    currentlisteners?: number;
    [key: string]: any;
}

interface PlayerState {
    isPlaying: boolean;
    isBuffering: boolean;
    wasPlayingBeforeError: boolean;
    currentData: StatsData;
}

export default function Player(): JSX.Element {
    const [state, setState] = useState<PlayerState>({
        isPlaying: false,
        isBuffering: false,
        wasPlayingBeforeError: false,
        currentData: {}
    });

    const audioRef = useRef<HTMLAudioElement>(null);
    const retryCountRef = useRef<number>(0);
    const retryTimeoutRef = useRef<number | null>(null);

    const updateMediaSession = useCallback((): void => {
        if (!('mediaSession' in navigator)) return;

        const { currentData, isPlaying } = state;
        const songInfo = currentData.songtitle
            ? decodeHtmlEntities(currentData.songtitle)
            : 'Live Stream';

        try {
            navigator.mediaSession.metadata = new MediaMetadata({
                title: 'Radio Plava Laguna',
                artist: songInfo,
                artwork: [{
                    src: '/logo-circle.png',
                    sizes: '600x600',
                    type: 'image/png'
                }]
            });
            navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
        } catch (error) {
            console.error('Error updating media session:', error);
        }
    }, [state]);

    const attemptReconnect = useCallback((): void => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
        }

        const audio = audioRef.current;
        if (!audio || retryCountRef.current >= MAX_RETRIES) {
            retryCountRef.current = 0;
            setState(s => ({ ...s, wasPlayingBeforeError: false }));
            return;
        }

        const delay = Math.min(
            RETRY_BASE_DELAY * Math.pow(RETRY_MULTIPLIER, retryCountRef.current),
            MAX_RETRY_DELAY
        );

        console.log(`Retrying in ${delay}ms (attempt ${retryCountRef.current + 1})`);

        retryTimeoutRef.current = setTimeout(async () => {
            retryCountRef.current++;

            try {
                audio.load();
                await new Promise(resolve => setTimeout(resolve, 100));
                await audio.play();
                console.log("Reconnection successful!");
                setState(s => ({ ...s, wasPlayingBeforeError: false }));
            } catch (error) {
                console.error("Reconnection failed:", error);
                attemptReconnect();
            }
        }, delay) as unknown as number;
    }, []);

    const handleStreamError = useCallback((): void => {
        setState(s => {
            const wasPlaying = s.isPlaying;
            if (wasPlaying) {
                setTimeout(attemptReconnect, 0);
            }
            return { ...s, isPlaying: false, wasPlayingBeforeError: wasPlaying };
        });
    }, [attemptReconnect]);

    const togglePlayback = useCallback(async (): Promise<void> => {
        const audio = audioRef.current;
        if (!audio) return;

        if (state.isPlaying) {
            audio.pause();
            setState(s => ({ ...s, wasPlayingBeforeError: false }));
            return;
        }

        try {
            retryCountRef.current = 0;
            setState(s => ({ ...s, wasPlayingBeforeError: true }));
            audio.load();
            await audio.play();
        } catch (e) {
            console.error("Playback failed:", e);
            handleStreamError();
        }
    }, [state.isPlaying, handleStreamError]);

    useEffect(() => {
        if (!('mediaSession' in navigator)) return;

        const audio = audioRef.current;

        type MediaSessionAction = 'play' | 'pause' | 'previoustrack' | 'nexttrack' |
            'seekbackward' | 'seekforward' | 'seekto';

        const handlers: Record<MediaSessionAction, MediaSessionActionHandler | null> = {
            play: () => { audio?.play().catch(console.error); },
            pause: () => { audio?.pause(); },
            previoustrack: null,
            nexttrack: null,
            seekbackward: null,
            seekforward: null,
            seekto: null
        };

        Object.entries(handlers).forEach(([action, handler]) => {
            try {
                navigator.mediaSession.setActionHandler(
                    action as MediaSessionAction,
                    handler
                );
            } catch (error) {
                console.log(`Action '${action}' not supported`);
            }
        });
    }, []);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handlers: Record<string, EventListener> = {
            play: () => {
                console.log("Event: play");
                setState(s => ({ ...s, isPlaying: true, isBuffering: false }));
                retryCountRef.current = 0;
            },
            pause: () => {
                console.log("Event: pause");
                setState(s => ({ ...s, isPlaying: false }));
            },
            ended: () => {
                console.log("Event: ended");
                handleStreamError();
            },
            error: (e: Event) => {
                console.log("Event: error", e);
                handleStreamError();
            },
            waiting: () => {
                console.log("Event: waiting");
                setState(s => ({ ...s, isBuffering: true }));
            },
            canplay: () => {
                console.log("Event: canplay");
                setState(s => ({ ...s, isBuffering: false }));
            },
            stalled: () => {
                console.log("Event: stalled");
                setState(s => ({ ...s, isBuffering: true }));
                if (state.wasPlayingBeforeError) {
                    setTimeout(attemptReconnect, 2000);
                }
            }
        };

        const handleOnline = (): void => {
            console.log("Network: online");
            if (state.wasPlayingBeforeError && !state.isPlaying) {
                attemptReconnect();
            }
        };

        const handleOffline = (): void => {
            console.log("Network: offline");
        };

        Object.entries(handlers).forEach(([event, handler]) => {
            audio.addEventListener(event, handler);
        });
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            Object.entries(handlers).forEach(([event, handler]) => {
                audio.removeEventListener(event, handler);
            });
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
        };
    }, [state.wasPlayingBeforeError, state.isPlaying, handleStreamError, attemptReconnect]);

    useEffect(() => {
        const fetchStats = async (): Promise<void> => {
            try {
                const data = await getStats();
                setState(s => ({ ...s, currentData: data }));
            } catch (error) {
                console.error("Error fetching stats:", error);
            }
        };

        fetchStats();
        const interval = setInterval(fetchStats, 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        updateMediaSession();
    }, [state.currentData.songtitle, state.isPlaying, updateMediaSession]);

    const { currentData, isPlaying, isBuffering } = state;
    const title = currentData.songtitle && decodeHtmlEntities(currentData.songtitle);
    const next = currentData.nexttitle && decodeHtmlEntities(currentData.nexttitle);

    return (
        <div className="player">
            <p className="title">{title}</p>
            {next && <p className="next">Sledeća: {next}</p>}
            <p>Trenutnih slušalaca: {currentData.currentlisteners}</p>

            <button
                onClick={togglePlayback}
                className="control"
                disabled={isBuffering}
            >
                {isPlaying ? <PauseIcon size={32} /> : <PlayIcon size={32} />}
            </button>

            {isBuffering && <p className="buffering">Buffering...</p>}

            <audio
                ref={audioRef}
                src={getSongUrl()}
                preload="none"
                crossOrigin="anonymous"
            />
        </div>
    );
}